import { useFetch } from '../../../hooks/useFetch';
import {
  Employee,
  EmployeeEpi,
  EpiDeadlineItem,
  Epi,
  EpiDelivery,
  GroupedEmployee,
} from '../../../models/employees';
import { File } from '../../../models/file';
import { errorNotification } from '../../../providers/mantine-notifications';
import { translateServerHttpErrors } from '../../../utils/helpers';
import {
  UseAddEmployeeFilesProps,
  UseCreateEmployeeProps,
  UseGetEmployeeFilesProps,
  UseGetEmployeeProps,
} from '../../../utils/types/data/hooks/employee';
import { PaginatedResponse } from '../../../utils/types/data/pagination';
import {
  addEmployeeFile,
  createEmployeeRequest,
  createEpiCatalogRequest,
  deleteEpiCatalogRequest,
  deleteEpiDeliveryRequest,
  getEmployeeFiles,
  linkEpisRequest,
  listEpiCatalogRequest,
  listEpiDeadlinesRequest,
  listEpiDeliveriesRequest,
  listEpisGroupedRequest,
  listEpisRequest,
  markEpiAsDeliveredRequest,
  showEmployeeRequest,
  unlinkEpiRequest,
  updateEpiCatalogRequest,
} from '../../employees';

export function useGetEmployee() {
  const {
    fetchData,
    state: { data: response, error, loading },
  } = useFetch<Employee>();

  const fetch = ({ id, onSuccess }: UseGetEmployeeProps) =>
    fetchData({
      fetcher: () => showEmployeeRequest(id),
      onError: (error: any) => {
        errorNotification({
          title: 'Erro ao buscar funcionário.',
          message: translateServerHttpErrors(error, 'Tente novamente.'),
        });
      },
      onSuccess,
    });

  return {
    fetch,
    response,
    error,
    loading,
  };
}

export function useCreateEmployee() {
  const {
    fetchData,
    state: { data: response, error, loading },
  } = useFetch<Employee>();

  const fetch = ({ userId, data, onSuccess }: UseCreateEmployeeProps) =>
    fetchData({
      fetcher: () => createEmployeeRequest(userId, data),
      onError: (error: any) => {
        errorNotification({
          title: 'Erro ao criar funcionário.',
          message: translateServerHttpErrors(error, 'Tente novamente.'),
        });
      },
      onSuccess,
    });

  return {
    fetch,
    response,
    error,
    loading,
  };
}

export function useGetEmployeeFiles() {
  const {
    fetchData,
    state: { data: response, error, loading },
  } = useFetch<File[]>();

  const fetch = ({ employeeId, onSuccess }: UseGetEmployeeFilesProps) =>
    fetchData({
      fetcher: () => getEmployeeFiles(employeeId),
      onError: async (error: any) => {
        errorNotification({
          title: 'Erro buscar arquivos.',
          message: translateServerHttpErrors(error, 'Tente novamente'),
        });
      },
      onSuccess,
    });

  return {
    fetch,
    response,
    error,
    loading,
  };
}

export function useAddEmployeeFiles() {
  const {
    fetchData,
    state: { data: response, error, loading },
  } = useFetch<void>();

  const fetch = ({ employeeId, data, onSuccess }: UseAddEmployeeFilesProps) =>
    fetchData({
      fetcher: () => addEmployeeFile(employeeId, data),
      onError: async (error: any) => {
        errorNotification({
          title: 'Erro ao adicionar arquivos.',
          message: translateServerHttpErrors(error, 'Tente novamente'),
        });
      },
      onSuccess,
    });

  return {
    fetch,
    response,
    error,
    loading,
  };
}

export function useListEpis() {
  const {
    fetchData,
    state: { data: response, error, loading },
  } = useFetch<PaginatedResponse<EmployeeEpi>>();

  const fetch = (
    query?: {
      employeeId?: number;
      epiId?: number;
      page?: number;
      limit?: number;
      isDelivered?: boolean;
    },
    onSuccess?: (data: PaginatedResponse<EmployeeEpi>) => void,
  ) =>
    fetchData({
      fetcher: () => listEpisRequest(query),
      onError: (error: any) => {
        errorNotification({
          title: 'Erro ao buscar entregas de EPIs.',
          message: translateServerHttpErrors(error, 'Tente novamente.'),
        });
      },
      onSuccess,
    });

  return {
    fetch,
    response,
    error,
    loading,
  };
}

export function useMarkEpiAsDelivered() {
  const {
    fetchData,
    state: { error, loading },
  } = useFetch<void>();

  const deliver = (
    id: number,
    onSuccess?: () => void,
    body?: { reason?: string; quantity?: number; deliveredAt?: string },
  ) =>
    fetchData({
      fetcher: () => markEpiAsDeliveredRequest(id, body),
      onError: (error: any) => {
        errorNotification({
          title: 'Erro ao registrar entrega de EPI.',
          message: translateServerHttpErrors(error, 'Tente novamente.'),
        });
      },
      onSuccess: () => {
        if (onSuccess) onSuccess();
      },
    });

  return { deliver, error, loading };
}

export function useEpiCatalog() {
  const {
    fetchData: listFetch,
    state: { data: catalog, loading: listLoading },
  } = useFetch<Epi[]>();

  const {
    fetchData: createFetch,
    state: { loading: createLoading },
  } = useFetch<Epi>();
  const {
    fetchData: updateFetch,
    state: { loading: updateLoading },
  } = useFetch<Epi>();
  const {
    fetchData: deleteFetch,
    state: { loading: deleteLoading },
  } = useFetch<void>();

  const list = (onSuccess?: (data: Epi[]) => void) =>
    listFetch({
      fetcher: () => listEpiCatalogRequest(),
      onSuccess,
    });

  const create = (data: Partial<Epi>, onSuccess?: (data: Epi) => void) =>
    createFetch({
      fetcher: () => createEpiCatalogRequest(data),
      onSuccess,
      onError: () =>
        errorNotification({
          title: 'Erro ao criar EPI.',
          message: 'Verifique os dados e tente novamente.',
        }),
    });

  const update = (id: number, data: Partial<Epi>, onSuccess?: () => void) =>
    updateFetch({
      fetcher: () => updateEpiCatalogRequest(id, data),
      onSuccess,
      onError: () =>
        errorNotification({
          title: 'Erro ao atualizar EPI.',
          message: 'Verifique os dados e tente novamente.',
        }),
    });

  const remove = (id: number, onSuccess?: () => void) =>
    deleteFetch({
      fetcher: () => deleteEpiCatalogRequest(id),
      onSuccess,
      onError: () =>
        errorNotification({
          title: 'Erro ao remover EPI.',
          message: 'Tente novamente.',
        }),
    });

  return {
    list,
    catalog,
    loading: listLoading || createLoading || updateLoading || deleteLoading,
    create,
    update,
    remove,
  };
}

export function useLinkEpis() {
  const {
    fetchData,
    state: { loading },
  } = useFetch<void>();

  const link = (
    data: { employeeIds: number[]; epiIds: number[] },
    onSuccess?: () => void,
  ) =>
    fetchData({
      fetcher: () => linkEpisRequest(data),
      onError: (error: any) => {
        errorNotification({
          title: 'Erro ao vincular EPIs.',
          message: translateServerHttpErrors(error, 'Tente novamente.'),
        });
      },
      onSuccess,
    });

  return {
    link,
    loading,
  };
}

export function useUnlinkEpi() {
  const {
    fetchData,
    state: { loading },
  } = useFetch<void>();

  const unlink = (id: number, onSuccess?: () => void) =>
    fetchData({
      fetcher: () => unlinkEpiRequest(id),
      onError: (error: any) => {
        errorNotification({
          title: 'Erro ao desvincular EPI.',
          message: translateServerHttpErrors(error, 'Tente novamente.'),
        });
      },
      onSuccess,
    });

  return {
    unlink,
    loading,
  };
}

export function useListEpiDeliveries() {
  const {
    fetchData,
    state: { data: response, error, loading },
  } = useFetch<PaginatedResponse<EpiDelivery>>();

  const fetch = (
    query?: {
      employeeId?: number;
      epiId?: number;
      startDate?: string;
      endDate?: string;
      page?: number;
      limit?: number;
    },
    onSuccess?: (data: PaginatedResponse<EpiDelivery>) => void,
  ) =>
    fetchData({
      fetcher: () => listEpiDeliveriesRequest(query),
      onError: (error: any) => {
        errorNotification({
          title: 'Erro ao buscar histórico de entregas de EPIs.',
          message: translateServerHttpErrors(error, 'Tente novamente.'),
        });
      },
      onSuccess,
    });

  return { fetch, response, error, loading };
}

export function useDeleteEpiDelivery() {
  const {
    fetchData,
    state: { loading },
  } = useFetch<void>();

  const remove = (id: number, onSuccess?: () => void) =>
    fetchData({
      fetcher: () => deleteEpiDeliveryRequest(id),
      onError: (error: any) => {
        errorNotification({
          title: 'Erro ao excluir registro de entrega.',
          message: translateServerHttpErrors(error, 'Tente novamente.'),
        });
      },
      onSuccess,
    });

  return { remove, loading };
}

export function useListEpisGrouped() {
  const {
    fetchData,
    state: { data: response, error, loading },
  } = useFetch<PaginatedResponse<GroupedEmployee>>();

  const fetch = (
    query?: {
      employeeId?: number;
      epiId?: number;
      sector?: string;
      role?: string;
      hasNoEpi?: boolean;
      hasPendingDelivery?: boolean;
      startDate?: string;
      endDate?: string;
      page?: number;
      limit?: number;
    },
    onSuccess?: (data: PaginatedResponse<GroupedEmployee>) => void,
  ) =>
    fetchData({
      fetcher: () => listEpisGroupedRequest(query),
      onError: (error: any) => {
        errorNotification({
          title: 'Erro ao buscar EPIs dos funcionários.',
          message: translateServerHttpErrors(error, 'Tente novamente.'),
        });
      },
      onSuccess,
    });

  return { fetch, response, error, loading };
}

export function useListEpiDeadlines() {
  const {
    fetchData,
    state: { data: response, loading },
  } = useFetch<{ items: EpiDeadlineItem[] }>();

  const fetch = () =>
    fetchData({
      fetcher: () => listEpiDeadlinesRequest(),
      onError: (error: any) => {
        errorNotification({
          title: 'Erro ao buscar prazos de EPIs.',
          message: translateServerHttpErrors(error, 'Tente novamente.'),
        });
      },
    });

  return { fetch, response, loading };
}
